import {
  Box,
  FormControlLabel,
  FormLabel,
  Grid,
  MenuItem,
  Radio,
  RadioGroup,
} from "@mui/material";
import { Formik } from "formik";
import { Form } from "react-router-dom";
import { toast } from "react-toastify";
import * as Yup from "yup";
import getAxiosError from "../../../app/api/axiosError";
import { addClient } from "../../../app/features/client/clientSlice";
import { RootState, useAppDispatch } from "../../../app/features/store/store";
import { AddClientRequest } from "../../../app/models/requestModels/clientRequest";
import FormikTextField from "../../../components/common/formikFields/FormikTextField";
import Loader from "../../../components/common/loader/Loader";
import SubmitModalWithFormik from "../../../components/common/modalPopup/SubmitModalWithFormik";
import { STATES_LIST } from "../../fdp/FdpConstants";
import {
  CITY_REGEX,
  ADDRESS_REGEX,
  ADDRESS_REGEX_INVALID_MSG,
  CITY_REGEX_INVALID_MSG,
  ZIPCODE_REGEX,
  ZIPCODE_REGEX_INVALID_MSG,
} from "../../../helpers/AppConstants";
import { useSelector } from "react-redux";

interface AddClientModalProps {
  closeAddClientModal: CallableFunction;
  onCompleted: CallableFunction;
}

function AddClientModelReq(
  clientUserAccount: string,
  clientName: string,
  clientDescription: string,
  billingAddress: string,
  city: string,
  state: string,
  zipcode: string,

  typeOfClient: string,
  dimWeightFactor: number | string,
  createdBy: string,
  updatedBy: string
): AddClientRequest {
  return {
    clientUserAccount: clientUserAccount,
    clientName: clientName,
    clientDescription: clientDescription,
    billingAddress: billingAddress,
    city: city,
    state: state,
    zipcode: zipcode,
  
    typeOfClient: typeOfClient,
    dimWeightFactor: dimWeightFactor,
    createdBy: createdBy,
    updatedBy: updatedBy,
  };
}
const AddClientModal = ({
  closeAddClientModal,
  onCompleted,
}: AddClientModalProps) => {
  const dispatch = useAppDispatch();

  const { authResponse } = useSelector((state: RootState) => state.login);

  // Initial values for the form fields
  const initialValues: AddClientRequest = {
    clientName: "",
    clientDescription: "",
    billingAddress: "",
    city: "",
    state: "",
  
    zipcode: "",
    typeOfClient: "",
    dimWeightFactor: "",
    createdBy: authResponse?.userSettings?.userName ?? "",
    updatedBy: authResponse?.userSettings?.userName ?? "",
  };

  // Validation schema for the form fields
  const validationSchema = Yup.object().shape({
    clientName: Yup.string().required("Client Name is required"),
    //minimumWaiveCount:Yup.number().required("Minimum Waive Count is required"),
    clientDescription: Yup.string(),
    billingAddress: Yup.string()
      .required("Billing Address is required")
      .matches(ADDRESS_REGEX, ADDRESS_REGEX_INVALID_MSG),
    city: Yup.string()
      .required("City is required")
      .matches(CITY_REGEX, CITY_REGEX_INVALID_MSG),
    state: Yup.string().required("State is required"),
    zipcode: Yup.string()
      .required("Zip Code is required")
      .matches(ZIPCODE_REGEX, ZIPCODE_REGEX_INVALID_MSG),
    dimWeightFactor: Yup.number(),
  });

  // Handle form submission
  const handleSubmit = async (
    values: AddClientRequest,
    { setSubmitting }: { setSubmitting: (isSubmitting: boolean) => void }
  ) => {
    try {
      const model = AddClientModelReq(
        values.clientName,
        values.clientName,
        values.clientDescription,
        values.billingAddress,
        values.city,
        values.state,
        values.zipcode,
     
        values.typeOfClient,
        values.dimWeightFactor === "" ? "0" : values.dimWeightFactor,
        values.createdBy,
        values.updatedBy
      );

      const response = await dispatch(addClient(model)).unwrap();
      if (response.isSuccess) {
        toast.success(response.message!, { toastId: "addClientSuccess" });
        onCompleted();
      } else {
        toast.error(response.message!, { toastId: "addClientError" });
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, { toastId: "addClientException" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Loader isLoading={false} />
      <Formik
        initialValues={{ ...initialValues }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting, values, setFieldValue }) => (
          <SubmitModalWithFormik
            title="Onboard a new client"
            closeSubmitModal={closeAddClientModal}
            childErrorMessage=""
            allowSubmit={true}
            size="lg"
            isLoading={isSubmitting}
            submitButtonName="Add"
            closeModalAfterSubmit={true}
          >
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Form>
                <Grid container rowSpacing={1} columnSpacing={2}>
                  <Grid item xs={12}>
                    <FormLabel component="legend">Choose Client Type</FormLabel>
                    <RadioGroup
                      row
                      name="clientType"
                      value={values.typeOfClient}
                      onChange={(event) =>
                        setFieldValue("typeOfClient", event.target.value)
                      }
                    >
                      <FormControlLabel
                        value="DEDICATED"
                        control={<Radio />}
                        label="Dedicated Delivery"
                      />
                      <FormControlLabel
                        value="E2E"
                        control={<Radio />}
                        label="Co-mingled Delivery"
                      />
                      <FormControlLabel
                        value="BOTH"
                        control={<Radio />}
                        label="Both Way Of Delivery"
                      />
                    </RadioGroup>
                  </Grid>

                  {values.typeOfClient && (
                    <>
                      <Grid item xs={12} sm={6}>
                        <FormikTextField
                          margin="normal"
                          id="clientName"
                          label="Client Name"
                          name="clientName"
                          size="small"
                          fullWidth
                          variant="outlined"
                          required
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormikTextField
                          margin="normal"
                          id="description"
                          label="Description"
                          name="clientDescription"
                          size="small"
                          fullWidth
                          variant="outlined"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormikTextField
                          margin="normal"
                          id="billingAddress"
                          label="Billing Address"
                          name="billingAddress"
                          size="small"
                          fullWidth
                          variant="outlined"
                          required
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormikTextField
                          margin="normal"
                          id="city"
                          label="City"
                          name="city"
                          size="small"
                          fullWidth
                          variant="outlined"
                          required
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormikTextField
                          select
                          margin="normal"
                          id="state"
                          label="State"
                          name="state"
                          size="small"
                          fullWidth
                          variant="outlined"
                          SelectProps={{
                            MenuProps: {
                              PaperProps: { style: { maxHeight: 150 } },
                            },
                          }}
                          required
                        >
                          {STATES_LIST.map((state, index) => (
                            <MenuItem key={index} value={state}>
                              {state}
                            </MenuItem>
                          ))}
                        </FormikTextField>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormikTextField
                          margin="normal"
                          id="zipcode"
                          label="Zip Code"
                          name="zipcode"
                          size="small"
                          fullWidth
                          variant="outlined"
                          required
                        />
                      </Grid>
                      {(values.typeOfClient === "E2E" ||
                        values.typeOfClient === "BOTH") && (
                        <Grid item xs={12} sm={6}>
                          <FormikTextField
                            margin="normal"
                            id="dimWeightFactor"
                            label="Dim Weight Factor"
                            name="dimWeightFactor"
                            size="small"
                            fullWidth
                            variant="outlined"
                          />
                        </Grid>
                      )}
                    </>
                  )}
                </Grid>
              </Form>
            </Box>
          </SubmitModalWithFormik>
        )}
      </Formik>
    </>
  );
};

export default AddClientModal;
